<!-- src/components/Landing/DashboardSetupChecklist.vue -->
<template>
  <div class="card dashboard-card">
    <div class="card-header d-flex align-items-center justify-content-between">
      <div>
        <h4 class="mb-0">Setup</h4>
        <small class="text-muted"
          >Quick checklist for dashboard configuration</small
        >
      </div>

      <span
        class="badge-pill"
        :class="openCount ? 'badge-pill--warn' : 'badge-pill--ok'"
      >
        {{ openCount ? openCount + " to do" : "All Set" }}
      </span>
    </div>

    <div class="card-body">
      <!-- <div class="intro text-muted">
        Welcome to the Sensoreal Admin Dashboard.
      </div> -->

      <div class="checklist">
        <!-- Rest Screen -->
        <div class="section">
          <div class="section-title">Rest Screen</div>
          <ul class="items">
            <li :class="{ done: !restBackgroundItems.length }">
              <div class="item-left">
                <span class="dot"></span>
                <span>Rest screen background missing</span>
              </div>

              <router-link
                v-if="restBackgroundItems.length"
                class="fix"
                :to="{ path: '/settings/rest', hash: 'background-media' }"
              >
                Fix
              </router-link>
            </li>
          </ul>
        </div>

        <!-- Page & Navigation -->
        <div class="section">
          <div class="section-title">Page & Navigation</div>
          <ul class="items">
            <li :class="{ done: !firstPageItems.length }">
              <div class="item-left">
                <span class="dot"></span>
                <span>No pages created</span>
              </div>

              <router-link
                v-if="firstPageItems.length"
                class="fix"
                to="/pages/new"
              >
                Fix
              </router-link>
            </li>

            <li :class="{ done: !navigationItems.length }">
              <div class="item-left">
                <span class="dot"></span>
                <span>No pages linked to a navigation item</span>
              </div>

              <router-link
                v-if="navigationItems.length"
                class="fix"
                to="/navigation"
              >
                Fix
              </router-link>
            </li>
          </ul>
        </div>

        <!-- Sidebar -->
        <div class="section">
          <div class="section-title">Sidebar</div>
          <ul class="items">
            <li :class="{ done: isGeneralSidebarLogoSet }">
              <div class="item-left">
                <span class="dot"></span>
                <span>Sidebar logo missing</span>
              </div>

              <router-link
                v-if="!isGeneralSidebarLogoSet"
                class="fix"
                :to="{ path: '/settings/general', hash: 'sidebar-logo' }"
              >
                Fix
              </router-link>
            </li>

            <li :class="{ done: isGeneralSidebarBrandLogoSet }">
              <div class="item-left">
                <span class="dot"></span>
                <span>Sidebar brand logo missing</span>
              </div>

              <router-link
                v-if="!isGeneralSidebarBrandLogoSet"
                class="fix"
                :to="{ path: '/settings/general', hash: 'sidebar-brand-logo' }"
              >
                Fix
              </router-link>
            </li>

            <li :class="{ done: isGeneralSidebarHeaderColourSet }">
              <div class="item-left">
                <span class="dot"></span>
                <span>Sidebar header colour missing</span>
              </div>

              <router-link
                v-if="!isGeneralSidebarHeaderColourSet"
                class="fix"
                :to="{
                  path: '/settings/general',
                  hash: 'sidebar-header-colour'
                }"
              >
                Fix
              </router-link>
            </li>

            <li :class="{ done: isGeneralSidebarDescriptionColourSet }">
              <div class="item-left">
                <span class="dot"></span>
                <span>Sidebar description colour missing</span>
              </div>

              <router-link
                v-if="!isGeneralSidebarDescriptionColourSet"
                class="fix"
                :to="{
                  path: '/settings/general',
                  hash: 'sidebar-description-colour'
                }"
              >
                Fix
              </router-link>
            </li>
          </ul>
        </div>

        <!-- Live Section -->
        <div class="section">
          <div class="section-title">Live Section</div>
          <ul class="items">
            <li :class="{ done: isLiveSelectionBackgroundSet }">
              <div class="item-left">
                <span class="dot"></span>
                <span>Live selection background missing</span>
              </div>

              <router-link
                v-if="!isLiveSelectionBackgroundSet"
                class="fix"
                :to="{ path: '/settings/live', hash: 'selection-background' }"
              >
                Fix
              </router-link>
            </li>

            <li :class="{ done: isLiveHeadingSet }">
              <div class="item-left">
                <span class="dot"></span>
                <span>Live sidebar heading missing</span>
              </div>

              <router-link
                v-if="!isLiveHeadingSet"
                class="fix"
                :to="{ path: '/live/masterplan', hash: 'sidebar-heading' }"
              >
                Fix
              </router-link>
            </li>

            <li :class="{ done: isLiveDescriptionSet }">
              <div class="item-left">
                <span class="dot"></span>
                <span>Live sidebar description missing</span>
              </div>

              <router-link
                v-if="!isLiveDescriptionSet"
                class="fix"
                :to="{ path: '/live/masterplan', hash: 'sidebar-description' }"
              >
                Fix
              </router-link>
            </li>

            <li :class="{ done: isLivingOptionSet }">
              <div class="item-left">
                <span class="dot"></span>
                <span>No living option created</span>
              </div>

              <router-link
                v-if="!isLivingOptionSet"
                class="fix"
                :to="{ path: '/live/masterplan', hash: 'living-options' }"
              >
                Fix
              </router-link>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import {
  FETCH_CONFIG_ITEMS,
  FETCH_NAVIGATION,
  FETCH_PAGES
} from "@/store/actions.type";

export default {
  name: "DashboardSetupChecklist",

  computed: {
    ...mapGetters(["configItems", "livingOptions", "navigation", "pages"]),

    // ---- original checks (kept) ----
    restBackgroundItems() {
      var restMedia = (this.configItems || []).find(function(item) {
        return ["restMedia"].includes(item.key);
      });

      return restMedia && restMedia.value && restMedia.value.length
        ? []
        : ["No background selected"];
    },

    firstPageItems() {
      return this.pages && this.pages.length ? [] : ["Page missing"];
    },

    navigationItems() {
      var isPageSet = false;

      if (this.navigation && this.navigation.categories) {
        if (
          this.navigation.categories[0] &&
          this.navigation.categories[0].children
        ) {
          this.navigation.categories[0].children.forEach(function(child) {
            if (child.pageId) isPageSet = true;
          });
        }
      }

      return isPageSet ? [] : ["No page attached to navigation item"];
    },

    isLiveSelectionBackgroundSet() {
      var item = (this.configItems || []).find(function(i) {
        return ["liveHereSelectionBackground"].includes(i.key);
      });
      return item && item.value ? true : false;
    },

    isLiveHeadingSet() {
      var item = (this.configItems || []).find(function(i) {
        return ["liveHereHeading"].includes(i.key);
      });
      return item && item.value && item.value.en ? true : false;
    },

    isLiveDescriptionSet() {
      var item = (this.configItems || []).find(function(i) {
        return ["liveHereDescription"].includes(i.key);
      });
      return item && item.value && item.value.en ? true : false;
    },

    isLivingOptionSet() {
      return this.livingOptions && this.livingOptions.length ? true : false;
    },

    isGeneralSidebarLogoSet() {
      var item = (this.configItems || []).find(function(i) {
        return ["generalSidebarLogo"].includes(i.key);
      });
      return item && item.value ? true : false;
    },

    isGeneralSidebarBrandLogoSet() {
      var item = (this.configItems || []).find(function(i) {
        return ["generalSidebarBrandLogo"].includes(i.key);
      });
      return item && item.value ? true : false;
    },

    isGeneralSidebarHeaderColourSet() {
      var item = (this.configItems || []).find(function(i) {
        return ["generalSidebarHeaderColour"].includes(i.key);
      });
      return item && item.value ? true : false;
    },

    isGeneralSidebarDescriptionColourSet() {
      var item = (this.configItems || []).find(function(i) {
        return ["generalSidebarDescriptionColour"].includes(i.key);
      });
      return item && item.value ? true : false;
    },

    // ---- dashboard-only helper: count remaining tasks ----
    openCount() {
      var n = 0;
      if (this.restBackgroundItems.length) n += 1;
      if (this.firstPageItems.length) n += 1;
      if (this.navigationItems.length) n += 1;

      if (!this.isGeneralSidebarLogoSet) n += 1;
      if (!this.isGeneralSidebarBrandLogoSet) n += 1;
      if (!this.isGeneralSidebarHeaderColourSet) n += 1;
      if (!this.isGeneralSidebarDescriptionColourSet) n += 1;

      if (!this.isLiveSelectionBackgroundSet) n += 1;
      if (!this.isLiveHeadingSet) n += 1;
      if (!this.isLiveDescriptionSet) n += 1;
      if (!this.isLivingOptionSet) n += 1;

      return n;
    }
  },

  created() {
    this.$store.dispatch(FETCH_CONFIG_ITEMS);
    this.$store.dispatch("FETCH_LIVING_OPTIONS"); // kept as per your Setup.vue
    this.$store.dispatch(FETCH_PAGES);
    this.$store.dispatch(FETCH_NAVIGATION);
  }
};
</script>

<style scoped lang="scss">
.dashboard-card {
  height: 100%;
}

.intro {
  font-size: 12px;
  margin-bottom: 10px;
}

.checklist {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 400px;
  overflow-y: auto;
  padding-right: 4px; /* small breathing room from scrollbar */
}

.section {
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 10px;
  padding: 10px 12px;
}

.section-title {
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 8px;
}

.items {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.items li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.item-left {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #374151;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 99px;
  background: #26c281;
  border-color: #26c281;
  opacity: 0.8;
}

/* Fix link */
.fix {
  font-size: 12px;
  color: #26c281;
  text-decoration: underline;
  font-weight: 600;
}

.fix:hover {
  text-decoration: underline;
}

/* Done state (keeps layout but reduces prominence) */
.done {
  opacity: 0.35;
  text-decoration: line-through;
}

.done .dot {
  background: #474948;
  border-color: #474948;
}

.badge-pill {
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid transparent;
  white-space: nowrap;
}

.badge-pill--warn {
  background: #fff7ed;
  color: #9a3412;
  border-color: #fed7aa;
}

.badge-pill--ok {
  background: #ecfdf5;
  color: #065f46;
  border-color: #a7f3d0;
}
</style>
